import type { ProductLabelLayout, ProductLabelLayoutStyle } from './productLabelTypes';

export type LabelLayoutTemplate =
  | 'GRID_2X2'
  | 'GRID_3X2'
  | 'HORIZONTAL_SPLIT'
  | 'VERTICAL_STACK'
  | 'CIRCULAR'
  | 'SQUARE_CENTER'
  | 'BRAND_LEFT'
  | 'COMPLIANCE_GRID'
  | 'FIELD_ORDER'
  | 'CANVAS';

export const DEFAULT_LABEL_FIELD_ORDER: LabelFieldKey[] = [
  'brand',
  'image',
  'extraImage0',
  'extraImage1',
  'name',
  'price',
  'netQty',
  'category',
  'description',
  'benefits',
  'hsCode',
  'origin',
  'expiry',
  'packaging',
  'company',
  'marketing',
  'barcode',
  'sku',
  'legal',
  'footer',
];

export type LabelFieldKey =
  | 'brand'
  | 'name'
  | 'image'
  | 'extraImage0'
  | 'extraImage1'
  | 'price'
  | 'netQty'
  | 'sku'
  | 'barcode'
  | 'category'
  | 'description'
  | 'benefits'
  | 'hsCode'
  | 'origin'
  | 'expiry'
  | 'packaging'
  | 'company'
  | 'marketing'
  | 'legal'
  | 'footer';

export type LabelMetrics = {
  widthPx: number;
  heightPx: number;
  padding: number;
  gap: number;
  baseFont: number;
  smallFont: number;
  titleFont: number;
  imageSize: number;
  extraImageSize: number;
  barcodeHeight: number;
  isCircular: boolean;
  columns: number;
  rows: number;
};

export const LAYOUT_TEMPLATE_OPTIONS: Array<{
  value: LabelLayoutTemplate;
  name: string;
  description: string;
  columns: number;
  rows: number;
}> = [
  { value: 'SQUARE_CENTER', name: 'Square · centered', description: 'Balanced stack — works on any proportion', columns: 1, rows: 1 },
  { value: 'HORIZONTAL_SPLIT', name: 'Horizontal split', description: 'Brand left · details right', columns: 2, rows: 1 },
  { value: 'VERTICAL_STACK', name: 'Vertical stack', description: 'Top-to-bottom for tall labels', columns: 1, rows: 1 },
  { value: 'GRID_2X2', name: 'Grid 2×2', description: 'Four equal zones', columns: 2, rows: 2 },
  { value: 'GRID_3X2', name: 'Grid 3×2', description: 'Six zones for dense info', columns: 3, rows: 2 },
  { value: 'BRAND_LEFT', name: 'Brand column', description: 'Wide brand band + content', columns: 3, rows: 1 },
  { value: 'CIRCULAR', name: 'Circular', description: 'Round label — auto-scales inward', columns: 1, rows: 1 },
  { value: 'COMPLIANCE_GRID', name: 'Compliance grid', description: 'Regulatory fields prioritized', columns: 2, rows: 2 },
  {
    value: 'FIELD_ORDER',
    name: 'Custom arrangement',
    description: 'Drag elements on the label to reorder',
    columns: 1,
    rows: 1,
  },
  {
    value: 'CANVAS',
    name: 'Design studio',
    description: 'Free-form canvas — position shapes, text & images anywhere',
    columns: 1,
    rows: 1,
  },
];

export function layoutStyleToTemplate(style?: ProductLabelLayoutStyle): LabelLayoutTemplate {
  switch (style) {
    case 'wrap':
      return 'HORIZONTAL_SPLIT';
    case 'vertical':
      return 'VERTICAL_STACK';
    case 'nutrition':
      return 'COMPLIANCE_GRID';
    case 'sticker':
      return 'SQUARE_CENTER';
    case 'classic':
    default:
      return 'SQUARE_CENTER';
  }
}

export function resolveLayoutTemplate(layout: ProductLabelLayout): LabelLayoutTemplate {
  if (layout.layoutTemplate) return layout.layoutTemplate;
  return layoutStyleToTemplate(layout.layoutStyle);
}

export function countVisibleFields(
  layout: ProductLabelLayout,
  hasProductImage: boolean,
  hasExtraImages: number,
  flags: {
    hasPrice: boolean;
    hasNetQty: boolean;
    hasCategory: boolean;
    hasDescription: boolean;
    hasBenefits: boolean;
    hasHs: boolean;
    hasOrigin: boolean;
    hasExpiry: boolean;
    hasPackaging: boolean;
    hasCompany: boolean;
    hasMarketing: boolean;
    hasLegal: boolean;
    hasFooter: boolean;
  },
): number {
  let n = 0;
  if (layout.showBrand) n += 1;
  if (layout.showName) n += 1;
  if (layout.showImage && hasProductImage) n += 1;
  n += hasExtraImages;
  if (layout.showPrice && flags.hasPrice) n += 1;
  if (layout.showNetQuantity && flags.hasNetQty) n += 1;
  if (layout.showSku) n += 1;
  if (layout.showBarcode) n += 1;
  if (layout.showCategory && flags.hasCategory) n += 1;
  if (layout.showDescription && flags.hasDescription) n += 1;
  if (layout.showBenefits && flags.hasBenefits) n += 1;
  if (layout.showHsCode) n += 1;
  if (layout.showCountryOfOrigin) n += 1;
  if (layout.showExpiry && flags.hasExpiry) n += 1;
  if (layout.showPackaging && flags.hasPackaging) n += 1;
  if (layout.showCompanyInfo && flags.hasCompany) n += 1;
  if (layout.showMarketing && flags.hasMarketing) n += 1;
  if (flags.hasLegal) n += 1;
  if (flags.hasFooter) n += 1;
  return Math.max(n, 3);
}

export function computeLabelMetrics(
  widthPx: number,
  heightPx: number,
  template: LabelLayoutTemplate,
  fieldCount: number,
  userFontScale = 1,
): LabelMetrics {
  const opt = LAYOUT_TEMPLATE_OPTIONS.find((o) => o.value === template) ?? LAYOUT_TEMPLATE_OPTIONS[0];
  const isCircular = template === 'CIRCULAR';
  const effectiveW = isCircular ? Math.min(widthPx, heightPx) : widthPx;
  const effectiveH = isCircular ? Math.min(widthPx, heightPx) : heightPx;
  const area = effectiveW * effectiveH;
  const density = fieldCount / Math.max(area / 10000, 1);

  let baseFont = Math.sqrt(area) * 0.095;
  baseFont /= 1 + density * 0.22;
  baseFont *= userFontScale;
  baseFont = Math.max(5.5, Math.min(15, baseFont));

  const padding = Math.max(3, baseFont * 0.45);
  const gap = Math.max(2, baseFont * 0.3);
  const titleFont = Math.min(baseFont * 1.35, effectiveH * 0.14);
  const smallFont = Math.max(5, baseFont * 0.82);
  const imageSize = Math.min(effectiveW * 0.32, effectiveH * 0.28, baseFont * 5.5);
  const extraImageSize = Math.min(imageSize * 0.65, baseFont * 3.5);
  const barcodeHeight = Math.min(effectiveH * 0.22, baseFont * 3.2);

  return {
    widthPx,
    heightPx,
    padding,
    gap,
    baseFont,
    smallFont,
    titleFont,
    imageSize,
    extraImageSize,
    barcodeHeight,
    isCircular,
    columns: opt.columns,
    rows: opt.rows,
  };
}

export function gridAreasForTemplate(template: LabelLayoutTemplate): string | undefined {
  switch (template) {
    case 'HORIZONTAL_SPLIT':
      return '"brand info" / 38% 1fr';
    case 'BRAND_LEFT':
      return '"brand content content" / 28% 1fr 1fr';
    case 'GRID_2X2':
      return undefined;
    case 'GRID_3X2':
      return undefined;
    case 'COMPLIANCE_GRID':
      return '"compliance compliance" "details meta" / 1fr 1fr';
    default:
      return undefined;
  }
}

export function truncateToFit(text: string, maxChars: number): string {
  const t = text.trim();
  if (t.length <= maxChars) return t;
  return `${t.slice(0, Math.max(0, maxChars - 1)).trim()}…`;
}

/** Max characters per field based on label area and font size */
export function maxCharsForField(
  metrics: LabelMetrics,
  field: LabelFieldKey,
): number {
  const areaFactor = (metrics.widthPx * metrics.heightPx) / 10000;
  const base = Math.floor(12 + areaFactor * 8);
  if (field === 'description' || field === 'legal') return Math.floor(base * 2.2);
  if (field === 'name' || field === 'brand') return Math.floor(base * 1.1);
  if (field === 'barcode' || field === 'sku') return 24;
  return base;
}
